import React, { useEffect, useState, useMemo } from "react";
import { api, BRAND, formatApiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Save, Pencil, Trash2, Calendar, Users } from "lucide-react";
import { DatePicker } from "@/components/DatePicker";
import { useConfirm } from "@/components/ConfirmDialog";
import { registeredByDate } from "@/lib/eligibility";

const MONTHS_PT = [
  "janeiro", "fevereiro", "março", "abril", "maio", "junho",
  "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
];
function monthKey(iso) { return (iso || "").slice(0, 7); }
function monthLabel(key) {
  if (!key || key.length < 7) return key || "—";
  const y = key.slice(0, 4);
  const m = parseInt(key.slice(5, 7), 10);
  return `${MONTHS_PT[m - 1] || m}/${y}`;
}

export default function Attendance() {
  const [tab, setTab] = useState("register");
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [classId, setClassId] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [records, setRecords] = useState({});
  const [history, setHistory] = useState([]);
  const confirm = useConfirm();

  const load = async () => {
    try {
      const [c, s, h] = await Promise.all([
        api.get("/classes"),
        api.get("/students"),
        api.get("/attendance"),
      ]);
      setClasses(c.data);
      setStudents(s.data);
      setHistory(h.data);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);

  const classStudents = useMemo(
    () => students.filter(s => s.class_id === classId && s.status === "ativo" && registeredByDate(s, date)),
    [students, classId, date]
  );

  useEffect(() => {
    if (!classId) return;
    (async () => {
      try {
        const { data } = await api.get("/attendance", { params: { class_id: classId } });
        const existing = data.find((x) => x.date === date);
        const map = {};
        classStudents.forEach((s) => { map[s.id] = true; });
        if (existing) {
          existing.records.forEach(r => { map[r.student_id] = !!r.present; });
        }
        setRecords(map);
      } catch (err) { toast.error(formatApiError(err)); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId, date, students]);

  const toggle = (sid) => setRecords(prev => ({ ...prev, [sid]: !prev[sid] }));
  const setAll = (val) => {
    const m = {};
    classStudents.forEach(s => { m[s.id] = val; });
    setRecords(m);
  };

  const save = async () => {
    if (!classId) return toast.error("Selecione uma turma");
    try {
      await api.post("/attendance", {
        class_id: classId, date,
        records: classStudents.map(s => ({ student_id: s.id, present: !!records[s.id] })),
      });
      toast.success("Presença salva");
      const h = await api.get("/attendance");
      setHistory(h.data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openInRegister = (item) => {
    setClassId(item.class_id);
    setDate(item.date);
    setTab("register");
  };

  const del = async (item) => {
    const cls = classes.find(c => c.id === item.class_id);
    const ok = await confirm({
      title: "Excluir chamada?",
      description: `${cls?.name || "Turma"} — ${item.date}. A ação não pode ser desfeita.`,
      confirmText: "Excluir", destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/attendance/${item.id}`);
      toast.success("Chamada excluída");
      setHistory(h => h.filter(x => x.id !== item.id));
    } catch (err) { toast.error(formatApiError(err)); }
  };

  // Group history by month, sorted desc; each group sorted by date desc
  const grouped = useMemo(() => {
    const map = {};
    for (const h of history) {
      const k = monthKey(h.date);
      if (!map[k]) map[k] = [];
      map[k].push(h);
    }
    const keys = Object.keys(map).sort((a, b) => b.localeCompare(a));
    return keys.map(k => ({
      key: k,
      label: monthLabel(k),
      items: map[k].sort((a, b) => (b.date || "").localeCompare(a.date || "")),
    }));
  }, [history]);

  const className = (id) => classes.find(c => c.id === id)?.name || "Turma removida";

  return (
    <div data-testid="admin-attendance-page">
      <PageHeader eyebrow="Controle" title="Presença" subtitle="Registre a chamada e consulte todo o histórico por mês." mascot={BRAND.MANGUITA_COACH} />

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList>
          <TabsTrigger value="register" data-testid="attendance-tab-register">Registrar</TabsTrigger>
          <TabsTrigger value="history" data-testid="attendance-tab-history">Histórico ({history.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="register">
          <div className="vv-card p-4 mb-4 grid sm:grid-cols-3 gap-4">
            <div>
              <Label>Turma</Label>
              <Select value={classId} onValueChange={setClassId}>
                <SelectTrigger data-testid="attendance-class-select"><SelectValue placeholder="Selecione a turma" /></SelectTrigger>
                <SelectContent>
                  {classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name} • {c.category}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Data</Label>
              <DatePicker value={date} onChange={setDate} testId="attendance-date-input" />
            </div>
            <div className="flex items-end gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setAll(true)} disabled={!classStudents.length}>Marcar todos</Button>
              <Button variant="outline" className="flex-1" onClick={() => setAll(false)} disabled={!classStudents.length}>Desmarcar</Button>
            </div>
          </div>

          {!classId ? (
            <EmptyState title="Selecione uma turma" description="Escolha a turma para começar a marcar a presença." mascot={BRAND.MANGUITO} />
          ) : classStudents.length === 0 ? (
            <EmptyState title="Sem alunos ativos" description="Esta turma ainda não possui alunos ativos associados." />
          ) : (
            <div className="vv-card divide-y divide-border">
              {classStudents.map((s) => (
                <label key={s.id} className="flex items-center justify-between p-4 hover:bg-muted/40 transition-colors cursor-pointer" data-testid={`attendance-row-${s.id}`}>
                  <div>
                    <div className="font-semibold">{s.name}</div>
                    <div className="text-xs text-muted-foreground">{s.category} • {s.position || "—"}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-xs uppercase tracking-widest font-bold ${records[s.id] ? "text-[hsl(var(--vv-lime))]" : "text-muted-foreground"}`}>
                      {records[s.id] ? "Presente" : "Ausente"}
                    </span>
                    <Checkbox checked={!!records[s.id]} onCheckedChange={() => toggle(s.id)} data-testid={`attendance-check-${s.id}`} />
                  </div>
                </label>
              ))}
              <div className="p-4 flex justify-end">
                <Button onClick={save} className="bg-[hsl(var(--vv-lime))] text-[hsl(218_50%_10%)] font-bold uppercase" data-testid="save-attendance-button">
                  <Save className="size-4 mr-2" /> Salvar presença
                </Button>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="history">
          {history.length === 0 ? (
            <EmptyState title="Nenhuma chamada registrada" description="Registre a primeira chamada na aba 'Registrar'." mascot={BRAND.MANGUITA} />
          ) : (
            <div className="space-y-6">
              {grouped.map((g) => (
                <div key={g.key} data-testid={`attendance-month-${g.key}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <Calendar className="size-4 text-[hsl(var(--vv-yellow))]" />
                    <h3 className="text-lg font-black capitalize">{g.label}</h3>
                    <span className="text-xs text-muted-foreground">{g.items.length} chamada(s)</span>
                  </div>
                  <div className="vv-card divide-y divide-border">
                    {g.items.map((it) => {
                      const total = it.records?.length || 0;
                      const presentes = (it.records || []).filter(r => r.present).length;
                      const pct = total ? Math.round((presentes / total) * 100) : 0;
                      return (
                        <div key={it.id} className="flex items-center gap-3 p-3 sm:p-4" data-testid={`attendance-item-${it.id}`}>
                          <div className="flex-1 min-w-0">
                            <div className="font-black">{it.date}</div>
                            <div className="text-xs text-muted-foreground truncate">{className(it.class_id)}</div>
                          </div>
                          <div className="hidden sm:flex items-center gap-2 text-sm text-muted-foreground">
                            <Users className="size-3.5" />
                            <span className="whitespace-nowrap">{presentes}/{total}</span>
                          </div>
                          <span className={`vv-chip whitespace-nowrap ${
                            pct >= 75 ? "vv-chip-lime" : pct >= 50 ? "vv-chip-yellow" : "vv-chip-red"
                          }`}>{pct}%</span>
                          <div className="flex items-center gap-1 shrink-0">
                            <Button size="icon" variant="ghost" onClick={() => openInRegister(it)} data-testid={`attendance-edit-${it.id}`} title="Ver / Editar">
                              <Pencil className="size-4" />
                            </Button>
                            <Button size="icon" variant="ghost" className="text-destructive" onClick={() => del(it)} data-testid={`attendance-delete-${it.id}`} title="Excluir">
                              <Trash2 className="size-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
